import {
  Activity,
  BarChart3,
  BellRing,
  Boxes,
  Database,
  FileText,
  type LucideIcon,
  Megaphone,
  Settings2,
  SlidersHorizontal,
  TrendingUp,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';

export interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  badgeCount?: number;
  /** Omitted/true renders normally. Set false to hide the link from the sidebar for everyone, admins included. */
  enabled?: boolean;
  /** Hides the link for non-admins. The route itself enforces this too (see RequireAdmin) —
   *  this only keeps a non-admin from seeing the link in the first place. */
  adminOnly?: boolean;
}

export interface NavSection {
  label: string;
  items: NavItem[];
  /** Omitted/true renders normally. Set false to pull a section out of the sidebar without deleting
   *  its route/page code — flip back to true (or drop the field) to bring it back. */
  enabled?: boolean;
}

// Temporarily off per product request — the pages and routes underneath
// are untouched, just not linked from the sidebar. Flip to `true` to
// restore the "Analytics" section.
const ANALYTICS_SECTION_ENABLED = false;

// Hidden for every role (admin included): no sidebar link, and the
// /maintenance route redirects away (see router.tsx). The page code is kept —
// flip to `true` to bring it back.
export const MAINTENANCE_ENABLED = false;

/**
 * สร้าง NavSection Array ตามจำนวน unreadCount ที่รับเข้ามาแบบ Dynamic
 */
export const getNavSections = (unreadCount: number = 0): NavSection[] => [
  {
    label: 'Monitoring',
    items: [
      { label: '3D Plant View', path: '/plant-view', icon: Boxes },
      { label: 'Operator Dashboard', path: '/operator', icon: Activity },
      { label: 'Flow Totalizer', path: '/totalizer', icon: TrendingUp },
      { label: 'Data Logger', path: '/logger', icon: Database },
      // ✅ เปลี่ยนจาก 4 เป็น unreadCount ที่รับมาจากฟังก์ชัน
      { label: 'Notifications', path: '/notifications', icon: BellRing, badgeCount: unreadCount },
    ],
  },
  {
    label: 'Analytics',
    enabled: ANALYTICS_SECTION_ENABLED,
    items: [
      { label: 'Performance', path: '/performance', icon: BarChart3 },
      { label: 'Energy & Utility', path: '/energy', icon: Zap },
      { label: 'Reports', path: '/reports', icon: FileText },
    ],
  },
  {
    label: 'Management',
    items: [
      { label: 'Equipment', path: '/equipment', icon: Settings2 },
      { label: 'Maintenance', path: '/maintenance', icon: Wrench, enabled: MAINTENANCE_ENABLED },
      { label: 'Users & Roles', path: '/users', icon: Users, adminOnly: true },
      { label: 'Admin Notify', path: '/admin-notify', icon: Megaphone, adminOnly: true },
      { label: 'Settings', path: '/settings', icon: SlidersHorizontal },
    ],
  },
];

/** Every nav item regardless of section enablement — used for things like the topbar's page-title lookup, which should still resolve correctly if a disabled section's route is reached directly. */
export const ALL_NAV_ITEMS: NavItem[] = getNavSections().flatMap((s) => s.items);

export const getVisibleNavSections = (unreadCount: number = 0, isAdmin: boolean = false): NavSection[] =>
  getNavSections(unreadCount)
    .filter((s) => s.enabled !== false)
    .map((s) => ({ ...s, items: s.items.filter((i) => i.enabled !== false && (!i.adminOnly || isAdmin)) }));